import { useEffect, useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useReportDateRange } from "@/hooks/useDateRange";
import { fetchJSON } from "@/lib/api";

const REPORTS = [
  'All Credit Customers',
  'Receivables/Payables',
  'Revenue',
  'Net Profit',
  'Attendance',
  "Busi. Credit/Debit Flow",
  "Bowser Transaction's",
  'Customer Account Statement',
  'Daily Rate History',
  'Daily Stock/Sale Register',
  'DSR',
  'DSR Format Report',
  'Density Report',
  'Day Wise Stock Value',
  'Handling Loss Report',
  'Daily Business Summary',
  'Discounts Offered',
  'Expenditure',
  'Working Capital',
  // Revenue & profit summaries
  // Receivables/Payables provides a short balance summary
  'Taxation',
  'GST REPORT',
  'GST SALE REPORT',
  'TCS REPORT',
  'VAT REPORT',
  'VAT SUMMARY',
  'LFR REPORT',
  'TDS REPORT',
  'Guest Customer Sales',
  'Lubricants Stock',
  'Purchase',
  'Employee Status',
  'Employee Transactions',
  'Sales',
  'Stock Variation',
  'Swipe',
  'Vendor Transactions',
  'FeedBack',
  'Interest Transactions',
];

type Field = { key: string; label: string; type?: string };
type Section = { title?: string; fields: Field[] };
type SectionLike = Section | Field; // allow flat field arrays for simple reports

const reportFields: Record<string, SectionLike[]> = {
  "All Credit Customers": [
    {
      title: 'Credit Parties Summary', fields: [
        { key: 'from', label: 'From Date', type: 'date' },
        { key: 'to', label: 'To Date', type: 'date' },
        { key: 'detailed', label: 'Detailed View', type: 'checkbox' },
        { key: 'creditDue', label: 'Credit Due View', type: 'checkbox' },
      ]
    },
    {
      title: 'Day Wise Credit', fields: [
        { key: 'saleDate', label: 'Sale Date', type: 'date' },
        { key: 'byGroup', label: 'By Group', type: 'checkbox' },
        { key: 'shiftWise', label: 'Shift Wise', type: 'checkbox' },
      ]
    },
    {
      title: 'Month wise Recovery', fields: [
        { key: 'm_from', label: 'From Date', type: 'date' },
        { key: 'm_to', label: 'To Date', type: 'date' },
        { key: 'mode', label: 'Mode' },
        { key: 'm_shift', label: 'Shift Wise', type: 'checkbox' },
      ]
    },
    {
      title: 'Indent wise Credit bill', fields: [
        { key: 'i_from', label: 'From Date', type: 'date' },
        { key: 'i_to', label: 'To Date', type: 'date' },
        { key: 'organization', label: 'Organization' },
        { key: 'vehicle', label: 'Vehicle' },
      ]
    },
    {
      title: 'Balance', fields: [
        { key: 'balance', label: 'Customer Balance Report', type: 'label' },
      ]
    },
    {
      title: 'Credit Limit Crossed Report', fields: [
        { key: 'cl_label', label: 'Customer Balance Report', type: 'label' },
        { key: 'cl_from', label: 'From Date', type: 'date' },
        { key: 'cl_to', label: 'To Date', type: 'date' },
      ]
    },
    {
      title: 'Vehicle wise Summary', fields: [
        { key: 'v_from', label: 'From Date', type: 'date' },
        { key: 'v_to', label: 'To Date', type: 'date' },
      ]
    },
  ],
  // No separate Credit Parties Summary item in the menu per screenshot
  "Attendance": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'employee', label: 'Employee Name' },
    { key: 'shift', label: 'Shift Wise' },
  ],
  "Busi. Credit/Debit Flow": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'transactionType', label: 'Type' },
    { key: 'party', label: 'Party' },
  ],
  "Bowser Transaction's": [
    {
      title: 'Bowser Transactions', fields: [
        { key: 'from', label: 'From Date', type: 'date' },
        { key: 'to', label: 'To Date', type: 'date' },
        { key: 'shift', label: 'Shift Wise' },
        { key: 'bowser', label: 'Bowser List' },
      ]
    },
    {
      title: 'Bowser Day Transaction', fields: [
        { key: 'selectDate', label: 'Select Date', type: 'date' },
        { key: 'shift', label: 'Shift Wise' },
      ]
    },
  ],
  "Customer Account Statement": [
    {
      title: "Customer Account Statement",
      fields: [
        { key: 'from', label: 'From Date', type: 'date' },
        { key: 'to', label: 'To Date', type: 'date' },
        { key: 'organization', label: 'Organization' },
        { key: 'liquids', label: 'Liquids', type: 'checkbox' },
        { key: 'lubricants', label: 'Lubricants', type: 'checkbox' },
        { key: 'mileage', label: 'Mileage', type: 'checkbox' },
        { key: 'recovery', label: 'Recovery', type: 'checkbox' },
        { key: 'all', label: 'All', type: 'checkbox' },
      ]
    },
    {
      title: "Credit Sales Vehicle Wise Report",
      fields: [
        { key: 'v_from', label: 'From Date', type: 'date' },
        { key: 'v_to', label: 'To Date', type: 'date' },
        { key: 'v_organization', label: 'Choose Organization *' },
      ]
    },
    {
      title: "Customer Financial Summary Reports",
      fields: [
        { key: 'f_from', label: 'From Date', type: 'date' },
        { key: 'f_to', label: 'To Date', type: 'date' },
        { key: 'f_organization', label: 'Choose Organization *' },
        { key: 'f_type', label: 'Choose type *' },
      ]
    },
    {
      title: "Indent Wise Pending Balance",
      fields: [
        { key: 'i_organization', label: 'Choose Organization *' },
        { key: 'i_indentNo', label: 'Choose Indent No' },
      ]
    }
  ],
  "Daily Rate History": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'product', label: 'Product' },
  ],
  "Daily Stock/Sale Register": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "DSR": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'reportType', label: 'Report Type' },
    { key: 'product', label: 'Product' },
  ],
  "DSR Format Report": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'product', label: 'Product' },
  ],
  "Density Report": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'tank', label: 'Tank' },
    { key: 'product', label: 'Product' },
  ],
  "Day Wise Stock Value": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'stockType', label: 'Type' },
  ],
  "Handling Loss Report": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'product', label: 'Product' },
  ],
  "Daily Business Summary": [
    {
      title: "Day Busi. Summary",
      fields: [
        { key: "dbs_day_sheet", label: "Day sheet", type: "checkbox" },
        { key: "dbs_employee_sheet", label: "Employee Sheet", type: "checkbox" },
        { key: "dbs_day_business", label: "Day Business", type: "checkbox" },
        { key: "selectDate", label: "Select Date", type: "date" },
        { key: "shift", label: "Shift Wise", type: "checkbox" },
      ]
    },
    {
      title: "Daily Business Sheet",
      fields: [
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
      ]
    },
    {
      title: "Day Settlement",
      fields: [
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
      ]
    },
    {
      title: "Monthly Busi. Group Report",
      fields: [
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
      ]
    },
    {
      title: "Dealer Margin",
      fields: [
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
        { key: "dm_liquid", label: "Liquid Sales", type: "checkbox" },
        { key: "dm_lubricant", label: "Lubricant Sales", type: "checkbox" },
      ]
    },
    {
      title: "Balance Sheet",
      fields: [
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
      ]
    },
    {
      title: "Working Capital",
      fields: [
        { key: "wc_one_day", label: "One Day Report", type: "checkbox" },
        { key: "wc_monthly", label: "Monthly Report", type: "checkbox" },
        { key: "from", label: "From Date", type: "date" },
        { key: "to", label: "To Date", type: "date" },
      ]
    }
  ],
  "Working Capital": [
    { key: "wc_one_day", label: "One Day Report", type: "checkbox" },
    { key: "wc_monthly", label: "Monthly Report", type: "checkbox" },
    { key: "from", label: "From Date", type: "date" },
    { key: "to", label: "To Date", type: "date" },
  ],
  "Discounts Offered": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "Expenditure": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'shift', label: 'Shift Wise' },
    { key: 'flow', label: 'Flow' },
    { key: 'expenseType', label: 'Expense Type' },
  ],
  "Taxation": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'taxType', label: 'Tax Type' },
  ],
  "GST REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "GST SALE REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "TCS REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "VAT REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "VAT SUMMARY": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "LFR REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "TDS REPORT": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "Guest Customer Sales": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'shift', label: 'Shift Wise' },
    { key: 'mobile', label: 'Mobile Number' },
  ],
  "Lubricants Stock": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'product', label: 'Product' },
  ],
  "Purchase": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'vendor_type', label: 'Vendor Type' },
    { key: 'type', label: 'Type' },
  ],
  "Employee Status": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'employee', label: 'Employee' },
  ],
  "Employee Transactions": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'employee', label: 'Employee' },
  ],
  "Sales": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'product', label: 'Product' },
  ],
  "Stock Variation": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "Swipe": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'shift', label: 'Shift Wise' },
    { key: 'swipeMode', label: 'Swipe Mode' },
  ],
  "Vendor Transactions": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'vendor_type', label: 'Vendor Type' },
    { key: 'vendor', label: 'Vendor' },
  ],
  "FeedBack": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
  ],
  "Interest Transactions": [
    { key: 'from', label: 'From Date', type: 'date' },
    { key: 'to', label: 'To Date', type: 'date' },
    { key: 'party', label: 'Party' },
    { key: 'loan', label: 'Loan' },
  ],
};

export default function Reports() {
  // Use standardized date range hook with 12 months default
  const { fromDate, toDate, setFromDate, setToDate, isValidRange } = useReportDateRange('LAST_12_MONTHS');

  const [selected, setSelected] = useState<string | null>(REPORTS[0]);
  const sections = useMemo(() => {
    const raw = reportFields[selected as string];
    if (!raw || raw.length === 0) return [{ title: undefined, fields: [{ key: 'from', label: 'From Date', type: 'date' }, { key: 'to', label: 'To Date', type: 'date' }] }];
    // If first element has 'fields', treat as Section[]; else wrap as a single Section
    const first: any = raw[0] as any;
    if (first && typeof first === 'object' && 'fields' in first) {
      return raw as Section[];
    }
    return [{ title: undefined, fields: raw as Field[] }];
  }, [selected]);
  const [values, setValues] = useState<Record<string, any>>({});
  const { toast } = useToast();
  const [reportRows, setReportRows] = useState<any[] | null>(null);
  const [reportColumns, setReportColumns] = useState<string[] | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [bowsers, setBowsers] = useState<any[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<any[]>([]);
  const [indents, setIndents] = useState<string[]>([]);
  const [submittedSection, setSubmittedSection] = useState<string | null>(null);
  const [reportDataHeader, setReportDataHeader] = useState<any>({});

  const vendorTypes = ['All', 'Liquid', 'Lubricant', 'Other'];
  const businessTypes = ['Bank', 'Capital', 'Cash', 'Creditor', 'Owner', 'Tanker'];
  const swipeModes = ['Cash', 'Bank', 'Petrol', 'Swipe'];
  const taxOptions = [
    'GST Purchases',
    'GST Sales',
    'TCS',
    'TDS',
    'VAT',
    'LFR',
  ];

  const setVal = (k: string, v: any) => setValues(prev => ({ ...prev, [k]: v }));

  // Clear report when switching sections or filters
  useEffect(() => {
    setReportRows(null);
    setReportColumns(null);
    setSubmittedSection(null);
    setReportDataHeader({});
  }, [selected, values]);

  const groupRowsBy15Days = (rows: any[], openingBalance: number) => {
    if (!rows || rows.length === 0) return [];
    
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const periods: Record<string, { period: string, sale: number, paid: number }> = {};
    
    sorted.forEach(row => {
      const date = new Date(row.date);
      if (isNaN(date.getTime())) return;
      
      const year = date.getFullYear();
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthStr = monthNames[date.getMonth()];
      
      const day = date.getDate();
      let periodKey = "";
      let periodLabel = "";
      
      const lastDay = new Date(year, date.getMonth() + 1, 0).getDate();
      
      if (day <= 15) {
        periodKey = `${year}-${String(date.getMonth()+1).padStart(2, '0')}-H1`;
        periodLabel = `01-${monthStr}-${year} - 15-${monthStr}-${year}`;
      } else {
        periodKey = `${year}-${String(date.getMonth()+1).padStart(2, '0')}-H2`;
        periodLabel = `16-${monthStr}-${year} - ${String(lastDay).padStart(2, '0')}-${monthStr}-${year}`;
      }
      
      if (!periods[periodKey]) {
        periods[periodKey] = {
          period: periodLabel,
          sale: 0,
          paid: 0
        };
      }
      
      const debit = Number(row.debit) || 0;
      const credit = Number(row.credit) || 0;
      
      periods[periodKey].sale += debit;
      periods[periodKey].paid += credit;
    });
    
    const periodList = Object.keys(periods).sort().map(key => periods[key]);
    
    let due = openingBalance;
    return periodList.map(p => {
      due = due + p.sale - p.paid;
      return {
        period: p.period,
        sale: p.sale,
        paid: p.paid,
        due: due
      };
    });
  };

  const printReportHTML = (divId: string) => {
    const printContent = document.getElementById(divId);
    if (!printContent) return;
    
    const win = window.open('', '', 'height=700,width=900');
    if (!win) return;
    
    win.document.write('<html><head><title>Print Report</title>');
    win.document.write('<style>');
    win.document.write(`
      body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; color: #000; background: #fff; }
      table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
      th { background-color: #f5f5f5; font-weight: bold; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .font-bold { font-weight: bold; }
      .font-semibold { font-weight: 600; }
      .uppercase { text-transform: uppercase; }
      .grid { display: grid; }
      .grid-cols-2 { grid-template-columns: 1fr 1fr; }
      .gap-4 { gap: 16px; }
      .border-t { border-top: 1px solid #ddd; }
      .pt-4 { padding-top: 16px; }
      .pb-1 { padding-bottom: 4px; }
      .pb-4 { padding-bottom: 16px; }
      .mt-4 { margin-top: 16px; }
      .mt-12 { margin-top: 48px; }
      .pt-8 { padding-top: 32px; }
      .w-80 { width: 320px; }
      .w-64 { width: 256px; }
      .border { border: 1px solid #ddd; }
      .rounded { border-radius: 4px; }
      .p-4 { padding: 16px; }
      .space-y-2 > * + * { margin-top: 8px; }
      .space-y-6 > * + * { margin-top: 24px; }
      .flex { display: flex; }
      .justify-between { justify-content: space-between; }
      .text-emerald-600 { color: #059669; }
      @media print {
        body { padding: 0; }
        .no-print { display: none; }
      }
    `);
    win.document.write('</style></head><body>');
    win.document.write(printContent.innerHTML);
    win.document.write('</body></html>');
    
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  const downloadCSV = (rows: any[], filename: string) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: any) => {
      if (val === null || val === undefined) return "";
      const s = String(val).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csvContent = [
      headers.join(","),
      ...rows.map(r => headers.map(h => escape(r[h])).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename.toLowerCase().replace(/ /g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printDirectly = (title: string, rows: any[], header: any, formValues: any, preOpenedWindow?: Window | null) => {
    const win = preOpenedWindow || window.open('', '', 'height=700,width=900');
    if (!win) return;
    
    win.document.open();
    win.document.write('<html><head><title>Print Report</title>');
    win.document.write('<style>');
    win.document.write(`
      body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; color: #000; background: #fff; }
      table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
      th { background-color: #f5f5f5; font-weight: bold; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .font-bold { font-weight: bold; }
      .font-semibold { font-weight: 600; }
      .uppercase { text-transform: uppercase; }
      .grid { display: grid; }
      .grid-cols-2 { grid-template-columns: 1fr 1fr; }
      .gap-4 { gap: 16px; }
      .border-t { border-top: 1px solid #ddd; }
      .pt-4 { padding-top: 16px; }
      .pb-1 { padding-bottom: 4px; }
      .pb-4 { padding-bottom: 16px; }
      .mt-4 { margin-top: 16px; }
      .mt-12 { margin-top: 48px; }
      .pt-8 { padding-top: 32px; }
      .w-80 { width: 320px; }
      .w-64 { width: 256px; }
      .border { border: 1px solid #ddd; }
      .rounded { border-radius: 4px; }
      .p-4 { padding: 16px; }
      .flex { display: flex; }
      .justify-between { justify-content: space-between; }
      .text-emerald-600 { color: #059669; }
      .btn-bar { display: flex; gap: 8px; margin-bottom: 15px; }
      .btn { padding: 6px 12px; font-size: 11px; font-weight: 600; border: none; border-radius: 4px; color: #fff; background-color: #3b82f6; cursor: pointer; }
      .btn-close { background-color: #6b7280; }
      @media print {
        .no-print { display: none; }
        body { padding: 0; }
      }
      ${header.dynamicTemplateStyles || ''}
    `);
    win.document.write('</style></head><body>');

    let bodyHTML = '<div class="btn-bar no-print">' +
      '<button class="btn" onclick="window.print()">Print Report</button>' +
      '<button class="btn btn-close" onclick="window.close()" style="margin-left: 8px;">Close Window</button>' +
      '</div>';

    const orgDetails = header.orgDetails || {};
    const logoUrl = header.logoUrl || '/brand-logo.png';
    const headerHtml = header.dynamicTemplateHtml || '<table class="header-table" style="width:100%; border-collapse:collapse; margin-bottom:15px; border:none;">' +
      '<tr>' +
        '<td style="width:80px; vertical-align:middle; padding:4px; border:none;">' +
          '<img class="logo" src="' + logoUrl + '" alt="Logo" style="width:60px; height:60px; object-fit:contain;" />' +
        '</td>' +
        '<td style="vertical-align:top; padding:4px; text-align:left; border:none;">' +
          '<div class="pump-title" style="font-size:18px; font-weight:800; text-transform:uppercase; text-align:center;">' + (orgDetails.companyName || orgDetails.company_name || orgDetails.organization_name || "XYZ SERVICE CENTRE") + '</div>' +
          '<div class="pump-sub" style="font-size:11px; text-align:center; color:#4b5563; font-weight:500; margin: 2px 0;">' + (orgDetails.companyAddress || orgDetails.company_address || "") + '</div>' +
          '<div class="pump-sub" style="font-size:11px; text-align:center; color:#4b5563; font-weight:500; margin: 2px 0;">' +
            'GSTIN: ' + (orgDetails.gstNumber || orgDetails.gst_number || orgDetails.tin_gst_number || "") +
            ' | Phone: ' + (orgDetails.contactNumber || orgDetails.contact_number || orgDetails.phone_number || "") +
          '</div>' +
        '</td>' +
      '</tr>' +
    '</table>';

    const orgName = header.customer_name || formValues.organization || formValues.v_organization || formValues.f_organization || formValues.i_organization || '';
    
    let dateRange = '';
    if (formValues.from && formValues.to) {
      dateRange = formValues.from + ' To ' + formValues.to;
    } else if (formValues.v_from && formValues.v_to) {
      dateRange = formValues.v_from + ' To ' + formValues.v_to;
    } else if (formValues.f_from && formValues.f_to) {
      dateRange = formValues.f_from + ' To ' + formValues.f_to;
    }

    if (title === "Customer Account Statement") {
      const openBal = parseFloat(header.opening_balance || 0).toFixed(3);
      const totalQty = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3);
      const totalMisc = rows.reduce((sum, r) => sum + (Number(r.misc_charges) || 0), 0).toFixed(3);
      const totalSale = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0).toFixed(3);
      const totalCol = rows.reduce((sum, r) => sum + (Number(r.collectable) || 0), 0).toFixed(3);
      const totalPay = rows.reduce((sum, r) => sum + (Number(r.payment) || 0), 0).toFixed(3);
      const closingBal = parseFloat(rows[rows.length - 1]?.balance ?? header.opening_balance ?? 0).toFixed(3);

      let rowsHTML = '';
      rows.forEach((row, idx) => {
        rowsHTML += '<tr>' +
          '<td class="text-center">' + (idx + 1) + '</td>' +
          '<td>' + row.date + '</td>' +
          '<td style="white-space: pre-line; font-weight: bold;">' + row.ref_no + '</td>' +
          '<td>' + row.vehicle_no + '</td>' +
          '<td class="font-bold">' + row.product + '</td>' +
          '<td class="text-right">' + (row.price ? parseFloat(row.price).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right">' + (row.qty ? parseFloat(row.qty).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right">' + (row.misc_charges ? parseFloat(row.misc_charges).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold">' + (row.total ? parseFloat(row.total).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold">' + (row.collectable ? parseFloat(row.collectable).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold">' + (row.payment ? parseFloat(row.payment).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold">' + parseFloat(row.balance || 0).toFixed(3) + '</td>' +
          '<td>' + (row.description || '') + '</td>' +
          '</tr>';
      });

      bodyHTML += '<div class="space-y-6">' +
        headerHtml +
        '<div class="grid grid-cols-2 border-t pt-4" style="margin-top: 15px;">' +
          '<div><span class="font-semibold">Organization:</span> <span class="font-bold" style="font-size: 13px;">' + orgName + '</span></div>' +
          '<div class="text-right"><span class="font-semibold">Date Range:</span> ' + dateRange + '</div>' +
          (header.customer_mobile ? '<div style="margin-top: 5px;"><span class="font-semibold">Mobile:</span> ' + header.customer_mobile + '</div>' : '') +
        '</div>' +
        '<h3 class="text-center font-bold uppercase" style="background: #f5f5f5; border: 1px solid #ddd; padding: 6px; margin: 15px 0;">Account Statement</h3>' +
        '<table>' +
          '<thead>' +
            '<tr>' +
              '<th>S.No</th>' +
              '<th>Date</th>' +
              '<th>Ref No</th>' +
              '<th>Vehicle No</th>' +
              '<th>Product</th>' +
              '<th class="text-right">Price</th>' +
              '<th class="text-right">Qty (lts.)</th>' +
              '<th class="text-right">Misc.Charges</th>' +
              '<th class="text-right">Total</th>' +
              '<th class="text-right">Collectable</th>' +
              '<th class="text-right">Payment</th>' +
              '<th class="text-right">Balance</th>' +
              '<th>Description</th>' +
            '</tr>' +
          '</thead>' +
          '<tbody>' +
            '<tr class="font-semibold">' +
              '<td colspan="11">Opening Balance</td>' +
              '<td class="text-right font-bold">' + openBal + '</td>' +
              '<td></td>' +
            '</tr>' +
            rowsHTML +
            '<tr class="font-bold" style="background: #f9f9f9;">' +
              '<td colspan="6">Total :</td>' +
              '<td class="text-right">' + totalQty + '</td>' +
              '<td class="text-right">' + totalMisc + '</td>' +
              '<td class="text-right">' + totalSale + '</td>' +
              '<td class="text-right">' + totalCol + '</td>' +
              '<td class="text-right">' + totalPay + '</td>' +
              '<td class="text-right">' + closingBal + '</td>' +
              '<td></td>' +
            '</tr>' +
          '</tbody>' +
        '</table>' +
        '<div class="w-80 border rounded p-4 space-y-2" style="margin-top: 20px;">' +
          '<h4 class="font-bold border-b pb-1" style="margin: 0 0 8px 0;">Balance Payment Details</h4>' +
          '<div class="flex justify-between" style="margin-bottom: 4px;"><span>Open Bal.</span><span class="font-bold">' + openBal + '</span></div>' +
          '<div class="flex justify-between" style="margin-bottom: 4px;"><span>Collectable</span><span class="font-bold">' + totalCol + '</span></div>' +
          '<div class="flex justify-between" style="margin-bottom: 4px;"><span>Paid</span><span class="font-bold">' + totalPay + '</span></div>' +
          '<div class="flex justify-between border-t pt-1 font-bold" style="border-top: 1px solid #ddd; padding-top: 4px;"><span>Closing Bal.</span><span>' + closingBal + '</span></div>' +
        '</div>' +
      '</div>';
    } else if (title === "Credit Sales Vehicle Wise Report") {
      const totalQty = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3);
      const totalAmt = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(3);

      let rowsHTML = '';
      rows.forEach((row) => {
        rowsHTML += '<tr>' +
          '<td>' + row.date + '</td>' +
          '<td class="font-bold">' + row.product + '</td>' +
          '<td class="font-semibold">' + row.vehicle_no + '</td>' +
          '<td>' + (row.indent_no || row.bill_no || 'N/A') + '</td>' +
          '<td class="text-right">' + (row.qty ? parseFloat(row.qty).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right">' + (row.rate ? parseFloat(row.rate).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold">' + (row.amount ? parseFloat(row.amount).toFixed(3) : '0.00') + '</td>' +
          '</tr>';
      });

      bodyHTML += '<div class="space-y-6">' +
        headerHtml +
        '<div class="grid grid-cols-2 border-t pt-4" style="margin-top: 15px;">' +
          '<div><span class="font-semibold">Organization:</span> <span class="font-bold" style="font-size: 13px;">' + orgName + '</span></div>' +
          '<div class="text-right"><span class="font-semibold">Date Range:</span> ' + dateRange + '</div>' +
        '</div>' +
        '<h3 class="text-center font-bold uppercase" style="background: #f5f5f5; border: 1px solid #ddd; padding: 6px; margin: 15px 0;">Credit Sales Report</h3>' +
        '<table>' +
          '<thead>' +
            '<tr>' +
              '<th>Date</th>' +
              '<th>Product</th>' +
              '<th>Vehicle No</th>' +
              '<th>Receipt No</th>' +
              '<th class="text-right">Quantity</th>' +
              '<th class="text-right">Unit Price</th>' +
              '<th class="text-right">Amount</th>' +
            '</tr>' +
          '</thead>' +
          '<tbody>' +
            rowsHTML +
            '<tr class="font-bold" style="background: #f5f5f5;">' +
              '<td colspan="4">SUBTOTAL</td>' +
              '<td class="text-right">' + totalQty + '</td>' +
              '<td></td>' +
              '<td class="text-right">' + totalAmt + '</td>' +
            '</tr>' +
            '<tr class="font-bold" style="background: #eaeaea;">' +
              '<td colspan="4">GRAND TOTAL</td>' +
              '<td class="text-right">' + totalQty + '</td>' +
              '<td></td>' +
              '<td class="text-right">' + totalAmt + '</td>' +
            '</tr>' +
          '</tbody>' +
        '</table>' +
      '</div>';
    } else if (title === "Customer Financial Summary Reports") {
      const openBalNum = Number(header.opening_balance || 0);
      const grouped = groupRowsBy15Days(rows, openBalNum);
      const totalSale = grouped.reduce((sum: number, r: any) => sum + Number(r.sale || 0), 0).toFixed(3);
      const totalPaid = grouped.reduce((sum: number, r: any) => sum + Number(r.paid || 0), 0).toFixed(3);
      const closingBal = parseFloat(grouped[grouped.length - 1]?.due ?? header.opening_balance ?? 0).toFixed(3);

      let rowsHTML = '';
      grouped.forEach((row: any, idx: number) => {
        rowsHTML += '<tr>' +
          '<td class="text-center">' + (idx + 1) + '</td>' +
          '<td>' + row.period + '</td>' +
          '<td class="text-right">' + parseFloat(row.sale).toFixed(3) + '</td>' +
          '<td class="text-right">' + parseFloat(row.paid).toFixed(3) + '</td>' +
          '<td class="text-right font-bold">' + parseFloat(row.due).toFixed(3) + '</td>' +
          '</tr>';
      });

      bodyHTML += '<div class="space-y-6">' +
        headerHtml +
        '<div class="grid grid-cols-2 border-t pt-4" style="margin-top: 15px;">' +
          '<div><span class="font-semibold">Organization:</span> <span class="font-bold" style="font-size: 13px;">' + orgName + '</span></div>' +
          '<div class="text-right"><span class="font-semibold">Date Range:</span> ' + dateRange + '</div>' +
        '</div>' +
        '<h3 class="text-center font-bold uppercase" style="background: #f5f5f5; border: 1px solid #ddd; padding: 6px; margin: 15px 0;">Credit Sales 15 days</h3>' +
        '<table>' +
          '<thead>' +
            '<tr>' +
              '<th>S.No</th>' +
              '<th>Date / Period</th>' +
              '<th class="text-right">Sale Amount</th>' +
              '<th class="text-right">Paid Amount</th>' +
              '<th class="text-right">Due Amount</th>' +
            '</tr>' +
          '</thead>' +
          '<tbody>' +
            '<tr class="font-semibold">' +
              '<td colspan="4">Opening Bal</td>' +
              '<td class="text-right font-bold">' + openBalNum.toFixed(2) + '</td>' +
            '</tr>' +
            rowsHTML +
            '<tr class="font-bold" style="background: #f5f5f5;">' +
              '<td colspan="2">Total :</td>' +
              '<td class="text-right">' + totalSale + '</td>' +
              '<td class="text-right">' + totalPaid + '</td>' +
              '<td class="text-right">' + closingBal + '</td>' +
            '</tr>' +
          '</tbody>' +
        '</table>' +
        '<div style="margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-end;">' +
          '<div></div>' +
          '<div class="text-center" style="border-top: 1px solid #000; padding-top: 6px; width: 250px;">' +
            '<p class="font-bold" style="margin: 0;">FOR RAMKRISHNA SERVICE CENTRE</p>' +
            '<p style="margin: 2px 0 0 0; font-size: 10px; color: #555;">(Authorized Signature)</p>' +
          '</div>' +
        '</div>' +
      '</div>';
    } else if (title === "Indent Wise Pending Balance") {
      const totalQty = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3);
      const totalAmt = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(3);
      const totalPend = rows.reduce((sum, r) => sum + (Number(r.pending_balance) || 0), 0).toFixed(3);

      let rowsHTML = '';
      rows.forEach((row) => {
        rowsHTML += '<tr>' +
          '<td class="font-bold">' + row.indent_no + '</td>' +
          '<td>' + row.date + '</td>' +
          '<td>' + row.product + '</td>' +
          '<td class="text-right">' + (row.qty ? parseFloat(row.qty).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right">' + (row.amount ? parseFloat(row.amount).toFixed(3) : '0.00') + '</td>' +
          '<td class="text-right font-bold text-emerald-600">' + (row.pending_balance ? parseFloat(row.pending_balance).toFixed(3) : '0.00') + '</td>' +
          '</tr>';
      });

      bodyHTML += '<div class="space-y-6">' +
        headerHtml +
        '<div class="grid grid-cols-2 border-t pt-4" style="margin-top: 15px;">' +
          '<div><span class="font-semibold">Organization:</span> <span class="font-bold" style="font-size: 13px;">' + orgName + '</span></div>' +
          '<div class="text-right"><span class="font-semibold">Indent Selected:</span> ' + (formValues.i_indentNo || 'All') + '</div>' +
        '</div>' +
        '<h3 class="text-center font-bold uppercase" style="background: #f5f5f5; border: 1px solid #ddd; padding: 6px; margin: 15px 0;">Indent Wise Pending Balance</h3>' +
        '<table>' +
          '<thead>' +
            '<tr>' +
              '<th>Indent No</th>' +
              '<th>Date</th>' +
              '<th>Product</th>' +
              '<th class="text-right">Qty (lts)</th>' +
              '<th class="text-right">Amount</th>' +
              '<th class="text-right">Pending Balance</th>' +
            '</tr>' +
          '</thead>' +
          '<tbody>' +
            rowsHTML +
            '<tr class="font-bold" style="background: #f5f5f5;">' +
              '<td colspan="3">Total :</td>' +
              '<td class="text-right">' + totalQty + '</td>' +
              '<td class="text-right">' + totalAmt + '</td>' +
              '<td class="text-right text-emerald-600">' + totalPend + '</td>' +
            '</tr>' +
          '</tbody>' +
        '</table>' +
      '</div>';
    }

    win.document.write(bodyHTML);
    win.document.write('</body></html>');
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchJSON('/api/credit-sales');
        const result = await response.json();
        if (result.ok || result.success) {
          const rows = result.rows || [];
          const uniqueIndents = Array.from(new Set(rows.map((r: any) => r.indent_no || r.indentNo).filter(Boolean))) as string[];
          setIndents(uniqueIndents);
        }
      } catch (e) { /* ignore */ }
      try {
        const response = await fetchJSON('/api/credit-customers');
        const result = await response.json();
        if (result.ok) {
          setOrganizations(result.rows || []);
        }
      } catch (e) { /* ignore */ }
      try {
        const response = await fetchJSON('/api/vendors');
        const result = await response.json();
        if (result.ok) {
          setVendors(result.rows || []);
        }
      } catch (e) { /* ignore */ }
      try {
        const response = await fetchJSON('/api/employees');
        const result = await response.json();
        if (result.ok) {
          setEmployees(result.rows || []);
        }
      } catch (e) { /* ignore */ }
      try {
        const response = await fetchJSON('/api/fuel-products');
        const result = await response.json();
        if (result.ok) {
          setProducts(result.rows || []);
        }
      } catch (e) { /* ignore */ }
      try {
        // Tanks used as bowser list in several pages
        const response = await fetchJSON('/api/tanks');
        const result = await response.json();
        if (result.ok) {
          setBowsers(result.rows || []);
        }
      } catch (e) { /* ignore */ }
      try {
        const response = await fetchJSON('/api/expense-types');
        const result = await response.json();
        if (result.ok) {
          setExpenseTypes(result.rows || []);
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2 text-sm"><span className="font-semibold">Dashboard</span><span>/</span><span>Reports</span></div>

      <Card>
        <CardHeader>
          <CardTitle className="text-center text-orange-600 tracking-widest">REPORTS</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3">
              <div className="border rounded-md divide-y overflow-auto max-h-[60vh]">
                {REPORTS.map((label) => (
                   <button key={label} onClick={() => {
                     setSelected(label);
                     setReportRows(null);
                     setSubmittedSection(null);
                     setReportColumns([]);
                     setReportDataHeader({ opening_balance: 0, customer_mobile: "", customer_name: "" });
                   }} className={`w-full text-left px-3 py-2 hover:bg-muted text-sm ${selected === label ? 'bg-muted/60 font-medium' : ''}`}>
                     {label}
                   </button>
                ))}
              </div>
            </div>
            <div className="col-span-9">
              <div className="p-4 border rounded-md">
                <h3 className="font-semibold text-lg mb-4">{selected}</h3>
                <div className="space-y-6">
                  {sections.map((sec, idx) => (
                    <div key={idx} className="p-3 border rounded-lg bg-white/50">
                      {sec.title && <h4 className="font-medium text-sm mb-3">{sec.title}</h4>}
                      <div className="space-y-3">
                        {/* Render date pairs on a single row when two consecutive date fields exist */}
                        {(() => {
                          const elems: any[] = [];
                          const normalFields: any[] = [];
                          const checkboxFields: any[] = [];

                          sec.fields.forEach(f => {
                            if (f.type === 'checkbox') checkboxFields.push(f);
                            else normalFields.push(f);
                          });

                          // 1. Render normal fields
                          for (let i = 0; i < normalFields.length; i++) {
                            const f = normalFields[i];
                            if (f.type === 'label') {
                              elems.push(
                                <div key={f.key} className="py-3 text-sm text-muted-foreground border rounded px-2">{f.label}</div>
                              );
                              continue;
                            }

                            const next = normalFields[i + 1];
                            if (f.type === 'date' && next && next.type === 'date') {
                              elems.push(
                                <div key={f.key + "_pair"} className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm text-muted-foreground font-semibold">{f.label}</label>
                                    <Input type="date" value={values[f.key] || ''} onChange={(e: any) => setVal(f.key, e.target.value)} />
                                  </div>
                                  <div>
                                    <label className="text-sm text-muted-foreground font-semibold">{next.label}</label>
                                    <Input type="date" value={values[next.key] || ''} onChange={(e: any) => setVal(next.key, e.target.value)} />
                                  </div>
                                </div>
                              );
                              i++; // skip next
                              continue;
                            }

                            // render single normal field
                            elems.push(
                              <div key={f.key} className="space-y-1">
                                <label className="text-sm text-muted-foreground font-semibold">{f.label}</label>
                                {f.key.toLowerCase().includes('organization') ? (
                                  <Select value={values[f.key] || 'All'} onValueChange={(v) => setVal(f.key, v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Organization" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All Organizations</SelectItem>
                                      {organizations.map((o: any) => (<SelectItem key={o.id} value={String(o.id)}>{o.organization_name}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'vendor') ? (
                                  <Select value={values['vendor'] || 'All'} onValueChange={(v) => { setVal('vendor', v); setVal('party', v); }}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Vendor" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All Vendors</SelectItem>
                                      {vendors.map((v: any) => (<SelectItem key={v.id} value={String(v.id)}>{v.vendor_name}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'employee') ? (
                                  <Select value={values['employee'] || 'All'} onValueChange={(v) => setVal('employee', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Employee" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All Employees</SelectItem>
                                      {employees.map((e: any) => (<SelectItem key={e.id} value={String(e.id)}>{e.employee_name || e.name}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'product') ? (
                                  <Select value={values['product'] || 'All'} onValueChange={(v) => setVal('product', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Product" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All Products</SelectItem>
                                      {products.map((p: any) => (<SelectItem key={p.id} value={String(p.id)}>{p.product_name}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'bowser') ? (
                                  <Select value={values['bowser'] || ''} onValueChange={(v) => setVal('bowser', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Bowser" /></SelectTrigger>
                                    <SelectContent>
                                      {bowsers.map((b: any) => (<SelectItem key={b.id} value={String(b.id)}>{b.tank_number}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'tank') ? (
                                  <Select value={values['tank'] || ''} onValueChange={(v) => setVal('tank', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Tank" /></SelectTrigger>
                                    <SelectContent>
                                      {bowsers.map((b: any) => (<SelectItem key={b.id} value={String(b.id)}>{b.tank_number}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'mode') ? (
                                  <Select value={values['mode'] || ''} onValueChange={(v) => setVal('mode', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Mode" /></SelectTrigger>
                                    <SelectContent>
                                      {swipeModes.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'type' || f.key === 'f_type') ? (
                                  <Select value={values[f.key] || 'All'} onValueChange={(v) => setVal(f.key, v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Type" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All</SelectItem>
                                      <SelectItem value="Sale">Credit Sale</SelectItem>
                                      <SelectItem value="Recovery">Recovery</SelectItem>
                                      {f.key === 'type' && businessTypes.filter(t => t !== 'All').map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (f.key === 'i_indentNo') ? (
                                  <Select value={values['i_indentNo'] || 'All'} onValueChange={(v) => setVal('i_indentNo', v)}>
                                    <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Choose Indent No" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="All">All Indents</SelectItem>
                                      {indents.map((ind) => (<SelectItem key={ind} value={ind}>{ind}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input value={values[f.key] || ''} onChange={(e: any) => setVal(f.key, e.target.value)} />
                                )}
                              </div>
                            );
                          }

                          // 2. Render checkbox fields grouped together
                          if (checkboxFields.length > 0) {
                            elems.push(
                              <div key="checkbox_group" className="pt-2">
                                <div className="grid grid-cols-2 gap-3 pl-1">
                                  {checkboxFields.map(f => (
                                    <div key={f.key} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`chk_${f.key}`}
                                        checked={!!values[f.key]}
                                        onChange={(e: any) => setVal(f.key, e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                      />
                                      <label htmlFor={`chk_${f.key}`} className="text-sm text-gray-700 font-semibold cursor-pointer">{f.label}</label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }

                          return elems;
                        })()}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button onClick={async () => {
                          let printWin: Window | null = null;
                          if (selected === 'Customer Account Statement') {
                            printWin = window.open("", "_blank", "width=1200,height=800,scrollbars=yes,resizable=yes");
                            if (!printWin) {
                              alert("Popup blocker active! Please allow popups to print reports.");
                              return;
                            }
                            printWin.document.open();
                            printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preparing Report...</title>
                              <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f8f8;}
                              .msg{text-align:center;color:#555;}</style></head>
                              <body><div class="msg"><h3>⏳ Preparing Report...</h3><p>Please wait while we fetch and build the statement template.</p></div></body></html>`);
                            printWin.document.close();
                          }
                          try {
                            const sectionParams: Record<string, any> = {};
                            sec.fields.forEach(f => { sectionParams[f.key] = values[f.key]; });
                            // If this is the Credit Parties Summary section, fetch the actual report data
                            if (selected === 'All Credit Customers' && sec.title && sec.title.toLowerCase().includes('credit')) {
                              // build query with optional from/to
                              const q: string[] = [];
                              if (values['from']) q.push(`from=${encodeURIComponent(values['from'])}`);
                              if (values['to']) q.push(`to=${encodeURIComponent(values['to'])}`);
                              const url = '/api/reports/all-credit-customers' + (q.length ? `?${q.join('&')}` : '');
                              const r = await fetchJSON(url);
                              const j = await r.json();
                              if (j.ok) {
                                setReportRows(j.rows || []);
                                setReportColumns(['organization_name', 'phone_number', 'mobile_number', 'credit_limit', 'current_balance']);
                                toast({ title: 'Report loaded' });
                              } else {
                                toast({ variant: 'destructive', title: 'Failed', description: j.error || 'Unknown' });
                              }
                            } else {
                              // Real-time endpoints for some reports
                              const lowerTitle = (sec.title || '').toLowerCase();
                              const qs: string[] = [];
                              if (values['from']) qs.push(`from=${encodeURIComponent(values['from'])}`);
                              if (values['to']) qs.push(`to=${encodeURIComponent(values['to'])}`);
                              // swipe mode
                              if (selected === 'Swipe' && values['swipeMode'] && values['swipeMode'] !== 'All') qs.push(`mode=${encodeURIComponent(values['swipeMode'])}`);
                              // employee filters
                              if ((selected === 'Attendance' || selected === 'Employee Transactions') && values['employee'] && values['employee'] !== 'All') {
                                const v = values['employee'];
                                if (/^\d+$/.test(String(v))) qs.push(`employee_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`employee=${encodeURIComponent(String(v))}`);
                              }
                              // vendor filters
                              if (selected === 'Vendor Transactions') {
                                if (values['type'] && values['type'] !== 'All') qs.push(`type=${encodeURIComponent(values['type'])}`);
                                if (values['vendor_type'] && values['vendor_type'] !== 'All') qs.push(`vendor_type=${encodeURIComponent(values['vendor_type'])}`);
                                if (values['party'] && values['party'] !== 'All') qs.push(`party=${encodeURIComponent(values['party'])}`);
                              }
                              if (selected === 'Busi. Credit/Debit Flow') {
                                if (values['type'] && values['type'] !== 'All') qs.push(`type=${encodeURIComponent(values['type'])}`);
                                // allow business types mapping
                                if (values['party'] && values['party'] !== 'All') qs.push(`party=${encodeURIComponent(values['party'])}`);
                              }

                              let liveEndpoint: string | null = null;
                              if (selected === 'Swipe') liveEndpoint = '/api/reports/swipe';
                              if (selected === 'Vendor Transactions') liveEndpoint = '/api/reports/vendor-transactions';
                              if (selected === 'Attendance') liveEndpoint = '/api/reports/attendance';
                              if (selected === 'Busi. Credit/Debit Flow') liveEndpoint = '/api/reports/business-transactions';
                              if (selected === 'Sales') {
                                // Pass product id or name
                                if (values['product'] && values['product'] !== 'All') {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/sales';
                              }
                              if (selected === 'Customer Account Statement') {
                                const title = sec.title;
                                if (title === "Customer Account Statement") {
                                  if (values['organization'] && values['organization'] !== 'All') {
                                    const v = values['organization'];
                                    if (/^\d+$/.test(String(v))) qs.push(`organization_id=${encodeURIComponent(String(v))}`);
                                    else qs.push(`organization=${encodeURIComponent(String(v))}`);
                                  }
                                  if (values['liquids']) qs.push('liquids=true');
                                  if (values['lubricants']) qs.push('lubricants=true');
                                  if (values['recovery']) qs.push('recovery=true');
                                  if (values['all']) qs.push('all=true');
                                  if (values['mileage']) qs.push('mileage=true');
                                  liveEndpoint = '/api/reports/customer-account-statement';
                                } else if (title === "Credit Sales Vehicle Wise Report") {
                                  if (values['v_organization'] && values['v_organization'] !== 'All') {
                                    qs.push(`organization_id=${encodeURIComponent(String(values['v_organization']))}`);
                                  }
                                  if (values['v_from']) qs.push(`from=${encodeURIComponent(values['v_from'])}`);
                                  if (values['v_to']) qs.push(`to=${encodeURIComponent(values['v_to'])}`);
                                  liveEndpoint = '/api/reports/customer-account-statement/vehicle-wise';
                                } else if (title === "Customer Financial Summary Reports") {
                                  if (values['f_organization'] && values['f_organization'] !== 'All') {
                                    qs.push(`organization_id=${encodeURIComponent(String(values['f_organization']))}`);
                                  }
                                  if (values['f_from']) qs.push(`from=${encodeURIComponent(values['f_from'])}`);
                                  if (values['f_to']) qs.push(`to=${encodeURIComponent(values['f_to'])}`);
                                  if (values['f_type']) qs.push(`type=${encodeURIComponent(values['f_type'])}`);
                                  liveEndpoint = '/api/reports/customer-account-statement/financial-summary';
                                } else if (title === "Indent Wise Pending Balance") {
                                  if (values['i_organization'] && values['i_organization'] !== 'All') {
                                    qs.push(`organization_id=${encodeURIComponent(String(values['i_organization']))}`);
                                  }
                                  if (values['i_indentNo'] && values['i_indentNo'] !== 'All') {
                                    qs.push(`indent_no=${encodeURIComponent(String(values['i_indentNo']))}`);
                                  }
                                  liveEndpoint = '/api/reports/customer-account-statement/indent-wise';
                                }
                              }
                              if (selected === "Bowser Transaction's") {
                                if ((sec.title || '').toLowerCase().includes('bowser day')) {
                                  if (values['selectDate']) qs.push(`date=${encodeURIComponent(values['selectDate'])}`);
                                  liveEndpoint = '/api/reports/bowser-day';
                                } else {
                                  if (values['bowser']) {
                                    const v = values['bowser'];
                                    if (/^\d+$/.test(String(v))) qs.push(`bowser_id=${encodeURIComponent(String(v))}`);
                                    else qs.push(`bowser=${encodeURIComponent(String(v))}`);
                                  }
                                  liveEndpoint = '/api/reports/bowser-transactions';
                                }
                              }
                               if (selected === 'Daily Business Summary') {
                                 const lowerTitle = (sec.title || '').toLowerCase();
                                 if (lowerTitle.includes('settlement')) {
                                   liveEndpoint = '/api/day-settlements';
                                   if (values['from']) qs.push(`from_date=${encodeURIComponent(values['from'])}`);
                                   if (values['to']) qs.push(`to_date=${encodeURIComponent(values['to'])}`);
                                 } else {
                                   liveEndpoint = '/api/reports/daily-business-summary';
                                   if (values['selectDate']) qs.push(`date=${encodeURIComponent(values['selectDate'])}`);
                                   if (values['from']) qs.push(`from=${encodeURIComponent(values['from'])}`);
                                   if (values['to']) qs.push(`to=${encodeURIComponent(values['to'])}`);
                                 }
                               }
                              if (selected === 'Purchase') {
                                if (values['type']) qs.push(`type=${encodeURIComponent(values['type'])}`);
                                if (values['vendor_type']) qs.push(`vendor_type=${encodeURIComponent(values['vendor_type'])}`);
                                liveEndpoint = '/api/reports/product-purchases';
                              }
                              if (selected === 'Taxation') {
                                const t = (values['taxType'] || 'GST Purchases').toLowerCase();
                                if (t.includes('gst') && t.includes('purchase')) liveEndpoint = '/api/reports/gst-purchases';
                                else if (t.includes('gst') && t.includes('sale')) liveEndpoint = '/api/reports/gst-sales';
                                else if (t.includes('tcs')) liveEndpoint = '/api/reports/tcs';
                                else if (t.includes('tds')) liveEndpoint = '/api/reports/tds';
                                else if (t.includes('vat')) liveEndpoint = '/api/reports/vat';
                                else if (t.includes('lfr')) liveEndpoint = '/api/reports/lfr';
                                else liveEndpoint = '/api/reports/gst-purchases';
                              }
                              if (selected === 'GST REPORT') {
                                liveEndpoint = '/api/reports/gst-purchases';
                              }
                              if (selected === 'GST SALE REPORT') {
                                liveEndpoint = '/api/reports/gst-sales';
                              }
                              if (selected === 'TCS REPORT') {
                                liveEndpoint = '/api/reports/tcs';
                              }
                              if (selected === 'TDS REPORT') {
                                liveEndpoint = '/api/reports/tds';
                              }
                              if (selected === 'VAT REPORT' || selected === 'VAT SUMMARY') {
                                liveEndpoint = '/api/reports/vat';
                              }
                              if (selected === 'LFR REPORT') {
                                liveEndpoint = '/api/reports/lfr';
                              }
                              if (selected === 'Daily Rate History') {
                                if (values['product'] && values['product'] !== 'All') {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/product-rate';
                              }
                              if (selected === 'DSR' || selected === 'DSR Format Report') {
                                if (values['product']) {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                              }
                              if (selected === 'DSR') {
                                if (values['reportType']) qs.push(`reportType=${encodeURIComponent(values['reportType'])}`);
                                if (values['product']) {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/dsr';
                              }
                              if (selected === 'DSR Format Report') {
                                if (values['product']) {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/dsr-format';
                              }
                              if (selected === 'Day Wise Stock Value') {
                                if (values['type']) qs.push(`type=${encodeURIComponent(values['type'])}`);
                                liveEndpoint = '/api/reports/day-stock-value';
                              }
                              if (selected === 'Expenditure') {
                                if (values['flow'] && values['flow'] !== 'All') qs.push(`flow=${encodeURIComponent(values['flow'])}`);
                                if (values['expenseType'] && values['expenseType'] !== 'All') qs.push(`expenseType=${encodeURIComponent(values['expenseType'])}`);
                                liveEndpoint = '/api/reports/expenditure';
                              }
                              if (selected === 'Working Capital') {
                                // pass from/to already included
                                liveEndpoint = '/api/reports/working-capital';
                              }
                              // New summary endpoints
                              if (selected === 'Receivables/Payables') {
                                liveEndpoint = '/api/reports/receivables-payables';
                              }
                              if (selected === 'Revenue') {
                                liveEndpoint = '/api/reports/revenue';
                              }
                              if (selected === 'Net Profit') {
                                liveEndpoint = '/api/reports/net-profit';
                              }
                              if (selected === 'Employee Transactions') {
                                liveEndpoint = '/api/reports/employee-transactions';
                              }
                              if (selected === 'Guest Customer Sales') {
                                if (values['mobile']) qs.push(`mobile=${encodeURIComponent(values['mobile'])}`);
                                liveEndpoint = '/api/reports/guest-customer';
                              }
                              if (selected === 'Stock Variation') {
                                liveEndpoint = '/api/reports/stock-variation';
                              }
                              if (selected === 'Density Report') {
                                if (values['tank'] && values['tank'] !== 'All') qs.push(`tank_id=${encodeURIComponent(values['tank'])}`);
                                if (values['product'] && values['product'] !== 'All') {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/density-report';
                              }
                              if (selected === 'Handling Loss Report') {
                                if (values['product'] && values['product'] !== 'All') {
                                  const v = values['product'];
                                  if (/^\d+$/.test(String(v))) qs.push(`product_id=${encodeURIComponent(String(v))}`);
                                  else qs.push(`product=${encodeURIComponent(String(v))}`);
                                }
                                liveEndpoint = '/api/reports/handling-loss-report';
                              }
                              if (selected === 'Discounts Offered') {
                                liveEndpoint = '/api/reports/discounts';
                              }
                              if (selected === 'FeedBack') {
                                liveEndpoint = '/api/reports/feedback';
                              }
                              if (selected === 'Interest Transactions') {
                                liveEndpoint = '/api/reports/interest-transactions';
                              }
                              if (selected === 'Employee Status') {
                                liveEndpoint = '/api/reports/employee-status';
                              }
                              if (selected === 'Lubricants Stock') {
                                liveEndpoint = '/api/reports/lubricants-stock';
                              }
                              if (selected === 'Daily Stock/Sale Register') {
                                liveEndpoint = '/api/reports/dsr';
                              }
if (liveEndpoint) {
                                const url = liveEndpoint + (qs.length ? `?${qs.join('&')}` : '');
                                const r = await fetchJSON(url);
                                const j = await r.json();
                                if (j.ok) {
                                  if (selected === 'Customer Account Statement') {
                                     let dynamicTemplateHtml = '';
                                     let dynamicTemplateStyles = '';
                                     let orgDetails: any = {};
                                     let logoUrl = '/brand-logo.png';
                                     try {
                                       const orgRes = await fetchJSON("/api/organization-details");
                                       const orgJson = await orgRes.json();
                                       orgDetails = orgJson.rows?.[0] || {};
                                     } catch (e) {}
                                     try {
                                       const templatesRes = await fetchJSON("/api/print-templates");
                                       const templatesJson = await templatesRes.json();
                                       const templates = templatesJson.rows || [];
                                       const activeTemplate = templates.find((t: any) => t.type === "invoice" && t.isActive) || templates[0];
                                       
                                       if (activeTemplate) {
                                          const logoMap: Record<string, string> = {
                                            indianoil: "/logo-indianoil.svg",
                                            hp: "/logo-hp.svg",
                                            bpcl: "/logo-bpcl.svg",
                                            reliance: "/logo-reliance.svg",
                                            custom: "/brand-logo.png"
                                          };
                                          const logoType = activeTemplate.logoType || "custom";
                                          logoUrl = logoMap[logoType] || "/brand-logo.png";
                                          const isCustomLogo = !activeTemplate.logoType || activeTemplate.logoType === "custom";

                                          dynamicTemplateStyles = activeTemplate.styles || '';
                                          const parser = new DOMParser();
                                          const doc = parser.parseFromString(activeTemplate.htmlStructure || '', 'text/html');
                                          let combined = '';
                                          const topGstin = doc.querySelector('.hpbill-top-gstin');
                                          if (topGstin) combined += topGstin.outerHTML;
                                          const headerEl = doc.querySelector('.invoice-header, .receipt-header, .hpbill-header');
                                          if (headerEl) combined += headerEl.outerHTML;

                                          if (combined) {
                                            const compileData: any = {
                                              logoUrl,
                                              logoUrlLeft: isCustomLogo ? "/logo-indianoil.svg" : logoUrl,
                                              logoUrlRight: "",
                                              logoLeftStyle: "",
                                              logoRightStyle: "display: none;",
                                              logoRightContainerStyle: "display: none;",
                                              orgName: orgDetails.companyName || orgDetails.company_name || orgDetails.organization_name || "",
                                              orgAddress: orgDetails.companyAddress || orgDetails.company_address || "",
                                              orgGst: orgDetails.gstNumber || orgDetails.gst_number || orgDetails.tin_gst_number || "",
                                              orgContact: orgDetails.contactNumber || orgDetails.contact_number || orgDetails.phone_number || "",
                                            };
                                            
                                            Object.entries(compileData).forEach(([k, v]) => {
                                              const placeholder = new RegExp(`{{${k}}}`, "g");
                                              combined = combined.replace(placeholder, String(v));
                                            });
                                            dynamicTemplateHtml = combined;
                                          }
                                       }
                                     } catch (e) {}

                                     const headerWithOrg = { ...j, orgDetails, logoUrl, dynamicTemplateHtml, dynamicTemplateStyles };
                                     printDirectly(sec.title || '', j.rows || [], headerWithOrg, values, printWin);
                                  } else {
                                    setReportRows(j.rows || []);
                                    setSubmittedSection(sec.title || null);
                                    setReportDataHeader({
                                      opening_balance: j.opening_balance || 0,
                                      customer_mobile: j.customer_mobile || "",
                                      customer_name: j.customer_name || ""
                                    });
                                  }
                                  // set column order based on report
                                  if (selected === 'Swipe') setReportColumns(['transaction_date', 'swipe_type', 'swipe_mode', 'amount', 'batch_number', 'employee_name']);
                                  else if (selected === 'Vendor Transactions') setReportColumns(['transaction_date', 'vendor_name', 'vendor_type', 'transaction_type', 'amount', 'payment_mode', 'description']);
                                  else if (selected === 'Attendance') setReportColumns(['attendance_date', 'employee_name', 'status', 'shift_name']);
                                  else if (selected === 'Busi. Credit/Debit Flow') setReportColumns(['transaction_date', 'transaction_type', 'party_name', 'amount', 'description']);
                                  else if (selected === 'Sales') setReportColumns(['date', 'type', 'item', 'quantity', 'rate', 'amount']);
                                  else if (selected === 'Customer Account Statement') {
                                     const title = sec.title;
                                     if (title === "Customer Account Statement") setReportColumns(['date', 'organization_name', 'entry_type', 'product', 'amount', 'payment_mode']);
                                     else if (title === "Credit Sales Vehicle Wise Report") setReportColumns(['date', 'vehicle_no', 'product', 'qty', 'rate', 'amount', 'indent_no', 'bill_no']);
                                     else if (title === "Customer Financial Summary Reports") setReportColumns(['date', 'particulars', 'vehicle_no', 'indent_no', 'debit', 'credit', 'balance']);
                                     else if (title === "Indent Wise Pending Balance") setReportColumns(['indent_no', 'date', 'product', 'qty', 'amount', 'pending_balance']);
                                     else setReportColumns(['date', 'organization_name', 'entry_type', 'product', 'amount', 'payment_mode']);
                                   }
                                  else if (selected === 'Purchase') setReportColumns(['invoice_date', 'invoice_number', 'vendor_name', 'vendor_type', 'invoice_type', 'amount', 'gst_amount', 'total_amount', 'payment_status']);
                                  else if (selected === 'Daily Rate History') setReportColumns(['rate_date', 'product_name', 'open_rate', 'close_rate', 'variation_amount']);
                                  else if (selected === 'DSR' || selected === 'Daily Stock/Sale Register') setReportColumns(['date', 'product_name', 'total_qty', 'total_amount']);
                                  else if (selected === 'Discounts Offered') setReportColumns(['date', 'type', 'customer', 'discount_amount']);
                                  else if (selected === 'DSR Format Report') setReportColumns(['date', 'product_name', 'opening_reading', 'closing_reading', 'quantity', 'rate', 'amount']);
                                  else if (selected === 'Day Wise Stock Value') setReportColumns(['date', 'total_value']);
                                  else if (selected === 'Expenditure') setReportColumns(['expense_date', 'expense_type_name', 'flow_type', 'payment_mode', 'amount', 'employee_name']);
                                  else if (selected === 'Guest Customer Sales') setReportColumns(['sale_date', 'mobile_number', 'vehicle_number', 'product_name', 'quantity', 'price_per_unit', 'total_amount', 'payment_mode']);
                                  else if (selected === 'Stock Variation') setReportColumns(['date', 'product_name', 'opening_reading', 'closing_reading', 'quantity', 'variation']);
                                  else if (selected === 'Density Report') setReportColumns(['date', 'tank_number', 'product_name', 'temp', 'density', 'hydrometer']);
                                  else if (selected === 'Handling Loss Report') setReportColumns(['date', 'product_name', 'opening_stock', 'receipt', 'sales', 'closing_stock', 'actual_stock', 'handling_loss']);
                                  else if (selected === 'Receivables/Payables') setReportColumns(['total_receivables', 'total_payables']);
                                  else if (selected === 'Revenue') setReportColumns(['from', 'to', 'meter_sales', 'guest_sales', 'lub_sales', 'revenue']);
                                  else if (selected === 'Net Profit') setReportColumns(['from', 'to', 'meter_sales', 'guest_sales', 'lub_sales', 'revenue', 'total_expenses', 'cogs', 'net_profit']);
                                  else if (selected === "Bowser Transaction's") setReportColumns(['sale_date', 'product_name', 'before_dip_stock', 'gross_stock', 'tanker_sale_quantity', 'notes']);
                                  else if (selected === 'Taxation') {
                                    const t = (values['taxType'] || 'GST Purchases').toLowerCase();
                                    if (t.includes('gst') && t.includes('purchase')) setReportColumns(['invoice_date', 'vendor_name', 'invoice_number', 'amount', 'gst_amount', 'total_amount']);
                                    else if (t.includes('gst') && t.includes('sale')) setReportColumns(['product_name', 'total_sales', 'vat_amount']);
                                    else if (t.includes('tcs')) setReportColumns(['invoice_date', 'vendor_name', 'invoice_number', 'amount', 'gst_amount', 'tcs_amount', 'total_amount']);
                                    else if (t.includes('tds')) setReportColumns(['transaction_date', 'vendor_name', 'transaction_type', 'amount', 'tds_amount', 'payment_mode']);
                                    else if (t.includes('vat')) setReportColumns(['product_name', 'total_sales', 'vat_amount']);
                                    else if (t.includes('lfr')) setReportColumns([]);
                                  }
                                  else if (selected === 'GST REPORT') setReportColumns(['invoice_date', 'vendor_name', 'invoice_number', 'amount', 'gst_amount', 'total_amount']);
                                  else if (selected === 'GST SALE REPORT') setReportColumns(['product_name', 'total_sales', 'vat_amount']);
                                  else if (selected === 'TCS REPORT') setReportColumns(['invoice_date', 'vendor_name', 'invoice_number', 'amount', 'gst_amount', 'tcs_amount', 'total_amount']);
                                  else if (selected === 'TDS REPORT') setReportColumns(['transaction_date', 'vendor_name', 'transaction_type', 'amount', 'tds_amount', 'payment_mode']);
                                  else if (selected === 'VAT REPORT' || selected === 'VAT SUMMARY') setReportColumns(['product_name', 'total_sales', 'vat_amount']);
                                  else if (selected === 'LFR REPORT') setReportColumns([]);
                                   else if (selected === 'Daily Business Summary') {
                                     const lowerTitle = (sec.title || '').toLowerCase();
                                     if (lowerTitle.includes('settlement')) {
                                       setReportColumns(['settlement_date', 'opening_balance', 'meter_sale', 'lubricant_sale', 'total_sale', 'credit_amount', 'expenses', 'shortage', 'closing_balance', 'notes']);
                                     } else {
                                       setReportColumns(['date', 'opening_balance', 'meter_sale', 'lubricant_sale', 'total_sale', 'credit_amount', 'expenses', 'shortage', 'closing_balance', 'notes']);
                                     }
                                   }
                                  toast({ title: 'Report loaded' });
                                } else {
                                  if (printWin) printWin.close();
                                  toast({ variant: 'destructive', title: 'Failed', description: j.error || 'Unknown' });
                                }
                              } else {
                                const res = await fetchJSON('/api/reports/run', { method: 'POST', body: JSON.stringify({ report: selected, section: sec.title, params: sectionParams }) });
                                const j = await res.json();
                                if (j.ok) {
                                  toast({ title: 'Report queued' });
                                } else {
                                  if (printWin) printWin.close();
                                  toast({ variant: 'destructive', title: 'Failed', description: j.error || 'Unknown' });
                                }
                              }
                            }
                          } catch (e: any) {
                            if (printWin) printWin.close();
                            toast({ variant: 'destructive', title: 'Failed', description: e.message || String(e) });
                          }
                        }} className="bg-emerald-400">SUBMIT</Button>
                      </div>
                    </div>
                  ))}
                </div>
                 {reportRows && selected === 'Customer Account Statement' && submittedSection === "Customer Account Statement" && (
                  <div className="mt-6 border rounded-lg p-6 bg-white shadow-sm space-y-6">
                    <div className="flex justify-end gap-3 border-b pb-4 no-print">
                      <Button variant="outline" size="sm" onClick={() => downloadCSV(reportRows, 'Account Statement')}>Excel</Button>
                      <Button variant="outline" size="sm" onClick={() => printReportHTML('print-area-cas')}>Portrait</Button>
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm" onClick={() => printReportHTML('print-area-cas')}>PDF</Button>
                    </div>

                    <div id="print-area-cas" className="p-4 bg-white text-black font-sans space-y-6">
                      <div className="text-center space-y-1">
                        <h2 className="text-xl font-bold">RAMKRISHNA SERVICE CENTRE</h2>
                        <p className="text-sm">PRIYOR MORE.JOYNAGAR</p>
                        <p className="text-sm">24 PARGANA SOUTH</p>
                        <p className="text-sm">GSTIN: 19ABEPH6853K1Z9</p>
                        <p className="text-sm font-semibold">Mob: 9002296939</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                        <div>
                          <span className="font-semibold text-gray-600">Organization:</span> <span className="font-bold text-base">{reportDataHeader.customer_name || values.organization}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-600">Date Range:</span> <span className="font-semibold">{values.from || "Start"} To {values.to || "End"}</span>
                        </div>
                        {reportDataHeader.customer_mobile && (
                          <div>
                            <span className="font-semibold text-gray-600">Mobile:</span> <span className="font-semibold">{reportDataHeader.customer_mobile}</span>
                          </div>
                        )}
                      </div>

                      <h3 className="text-center text-md font-bold bg-gray-100 border py-1.5 uppercase tracking-wide">Account Statement</h3>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px] border-collapse border">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="border p-2 text-left">S.No</th>
                              <th className="border p-2 text-left font-semibold">Date</th>
                              <th className="border p-2 text-left font-semibold font-bold">Ref No</th>
                              <th className="border p-2 text-left font-semibold">Vehicle No</th>
                              <th className="border p-2 text-left font-semibold">Product</th>
                              <th className="border p-2 text-right font-semibold">Price</th>
                              <th className="border p-2 text-right font-semibold">Qty (lts.)</th>
                              <th className="border p-2 text-right font-semibold font-bold">Misc.Charges</th>
                              <th className="border p-2 text-right font-semibold">Total</th>
                              <th className="border p-2 text-right font-semibold">Collectable</th>
                              <th className="border p-2 text-right font-semibold">Payment</th>
                              <th className="border p-2 text-right font-semibold">Balance</th>
                              <th className="border p-2 text-left font-semibold">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border font-semibold">
                              <td className="p-2 border" colSpan={11}>Opening Balance</td>
                              <td className="p-2 border text-right font-bold">{parseFloat(reportDataHeader.opening_balance || 0).toFixed(3)}</td>
                              <td className="p-2 border"></td>
                            </tr>
                            {reportRows.map((row, idx) => (
                              <tr key={idx} className="border hover:bg-gray-50/50">
                                <td className="p-2 border text-center">{idx + 1}</td>
                                <td className="p-2 border whitespace-nowrap">{row.date}</td>
                                <td className="p-2 border whitespace-pre-line font-bold">{row.ref_no}</td>
                                <td className="p-2 border">{row.vehicle_no}</td>
                                <td className="p-2 border font-bold">{row.product}</td>
                                <td className="p-2 border text-right">{row.price ? parseFloat(row.price).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right">{row.qty ? parseFloat(row.qty).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right">{row.misc_charges ? parseFloat(row.misc_charges).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-bold">{row.total ? parseFloat(row.total).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-bold">{row.collectable ? parseFloat(row.collectable).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-bold">{row.payment ? parseFloat(row.payment).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-extrabold">{parseFloat(row.balance || 0).toFixed(3)}</td>
                                <td className="p-2 border font-semibold">{row.description}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 border font-bold text-gray-900">
                              <td className="p-2 border" colSpan={6}>Total :</td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.misc_charges) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.total) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.collectable) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.payment) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {parseFloat(reportRows[reportRows.length - 1]?.balance ?? reportDataHeader.opening_balance ?? 0).toFixed(3)}
                              </td>
                              <td className="p-2 border"></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="w-80 border rounded p-4 text-[11px] space-y-2 mt-4">
                        <h4 className="font-bold border-b pb-1">Balance Payment Details</h4>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Open Bal.</span>
                          <span className="font-bold">{parseFloat(reportDataHeader.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Collectable</span>
                          <span className="font-bold">{reportRows.reduce((sum, r) => sum + (Number(r.collectable) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Paid</span>
                          <span className="font-bold">{reportRows.reduce((sum, r) => sum + (Number(r.payment) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between border-t pt-1 font-bold text-xs">
                          <span>Closing Bal.</span>
                          <span>{parseFloat(reportRows[reportRows.length - 1]?.balance ?? reportDataHeader.opening_balance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {reportRows && selected === 'Customer Account Statement' && submittedSection === "Credit Sales Vehicle Wise Report" && (
                  <div className="mt-6 border rounded-lg p-6 bg-white shadow-sm space-y-6">
                    <div className="flex justify-end gap-3 border-b pb-4 no-print">
                      <Button variant="outline" size="sm" onClick={() => downloadCSV(reportRows, 'Credit Sales Report')}>Excel</Button>
                      <Button variant="outline" size="sm" onClick={() => printReportHTML('print-area-csvw')}>Portrait</Button>
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm" onClick={() => printReportHTML('print-area-csvw')}>PDF</Button>
                    </div>

                    <div id="print-area-csvw" className="p-4 bg-white text-black font-sans space-y-6">
                      <div className="text-center space-y-1">
                        <h2 className="text-xl font-bold">RAMKRISHNA SERVICE CENTRE</h2>
                        <p className="text-sm">PRIYOR MORE.JOYNAGAR</p>
                        <p className="text-sm">24 PARGANA SOUTH</p>
                        <p className="text-sm">GSTIN: 19ABEPH6853K1Z9</p>
                        <p className="text-sm font-semibold">Mob: 9002296939</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                        <div>
                          <span className="font-semibold text-gray-600">Organization:</span> <span className="font-bold text-base">{reportDataHeader.customer_name || values.v_organization}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-600">Date Range:</span> <span className="font-semibold">{values.v_from || "Start"} To {values.v_to || "End"}</span>
                        </div>
                      </div>

                      <h3 className="text-center text-md font-bold bg-gray-100 border py-1.5 uppercase tracking-wide">Credit Sales Report</h3>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px] border-collapse border">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="border p-2 text-left">Date</th>
                              <th className="border p-2 text-left">Product</th>
                              <th className="border p-2 text-left">Vehicle No</th>
                              <th className="border p-2 text-left">Receipt No</th>
                              <th className="border p-2 text-right">Quantity</th>
                              <th className="border p-2 text-right">Unit Price</th>
                              <th className="border p-2 text-right font-semibold">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportRows.map((row, idx) => (
                              <tr key={idx} className="border hover:bg-gray-50/50">
                                <td className="p-2 border whitespace-nowrap">{row.date}</td>
                                <td className="p-2 border font-bold">{row.product}</td>
                                <td className="p-2 border font-semibold">{row.vehicle_no}</td>
                                <td className="p-2 border">{row.indent_no || row.bill_no || "N/A"}</td>
                                <td className="p-2 border text-right">{row.qty ? parseFloat(row.qty).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right">{row.rate ? parseFloat(row.rate).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-bold">{row.amount ? parseFloat(row.amount).toFixed(3) : "0.00"}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-bold border">
                              <td className="p-2 border text-left" colSpan={4}>SUBTOTAL</td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border"></td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(3)}
                              </td>
                            </tr>
                            <tr className="bg-gray-100 font-extrabold border text-gray-900">
                              <td className="p-2 border text-left" colSpan={4}>GRAND TOTAL</td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border"></td>
                              <td className="p-2 border text-right font-bold">
                                {reportRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(3)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {reportRows && selected === 'Customer Account Statement' && submittedSection === "Customer Financial Summary Reports" && (
                  <div className="mt-6 border rounded-lg p-6 bg-white shadow-sm space-y-6">
                    <div className="flex justify-end gap-3 border-b pb-4 no-print">
                      <Button variant="outline" size="sm" onClick={() => downloadCSV(groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0)), 'Credit Sales 15 days')}>Excel</Button>
                      <Button variant="outline" size="sm" onClick={() => printReportHTML('print-area-cfs')}>Portrait</Button>
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm" onClick={() => printReportHTML('print-area-cfs')}>PDF</Button>
                    </div>

                    <div id="print-area-cfs" className="p-4 bg-white text-black font-sans space-y-6">
                      <div className="text-center space-y-1">
                        <h2 className="text-xl font-bold">RAMKRISHNA SERVICE CENTRE</h2>
                        <p className="text-sm">PRIYOR MORE.JOYNAGAR</p>
                        <p className="text-sm">24 PARGANA SOUTH</p>
                        <p className="text-sm">GSTIN: 19ABEPH6853K1Z9</p>
                        <p className="text-sm font-semibold">Mob: 9002296939</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                        <div>
                          <span className="font-semibold text-gray-600">Organization:</span> <span className="font-bold text-base">{reportDataHeader.customer_name || values.f_organization}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-600">Date Range:</span> <span className="font-semibold">{values.f_from || "Start"} To {values.f_to || "End"}</span>
                        </div>
                      </div>

                      <h3 className="text-center text-md font-bold bg-gray-100 border py-1.5 uppercase tracking-wide">Credit Sales 15 days</h3>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px] border-collapse border">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="border p-2 text-left">S.No</th>
                              <th className="border p-2 text-left">Date / Period</th>
                              <th className="border p-2 text-right">Sale Amount</th>
                              <th className="border p-2 text-right">Paid Amount</th>
                              <th className="border p-2 text-right">Due Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border font-semibold">
                              <td className="p-2 border" colSpan={4}>Opening Bal</td>
                              <td className="p-2 border text-right font-bold">{parseFloat(reportDataHeader.opening_balance || 0).toFixed(3)}</td>
                            </tr>
                            {groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0)).map((row: any, idx: number) => (
                              <tr key={idx} className="border hover:bg-gray-50/50">
                                <td className="p-2 border text-center">{idx + 1}</td>
                                <td className="p-2 border">{row.period}</td>
                                <td className="p-2 border text-right">{parseFloat(row.sale).toFixed(3)}</td>
                                <td className="p-2 border text-right">{parseFloat(row.paid).toFixed(3)}</td>
                                <td className="p-2 border text-right font-extrabold">{parseFloat(row.due).toFixed(3)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100 font-bold border">
                              <td className="p-2 border" colSpan={2}>Total :</td>
                              <td className="p-2 border text-right">
                                {groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0)).reduce((sum: number, r: any) => sum + Number(r.sale || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0)).reduce((sum: number, r: any) => sum + Number(r.paid || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right font-bold">
                                {parseFloat(groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0))[groupRowsBy15Days(reportRows, Number(reportDataHeader.opening_balance || 0)).length - 1]?.due ?? reportDataHeader.opening_balance ?? 0).toFixed(3)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-12 flex justify-between text-[11px] pt-8">
                        <div></div>
                        <div className="text-center border-t border-gray-400 pt-1.5 w-64">
                          <p className="font-bold">FOR RAMKRISHNA SERVICE CENTRE</p>
                          <p className="text-gray-500 text-[10px] mt-1">(Authorized Signature)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {reportRows && selected === 'Customer Account Statement' && submittedSection === "Indent Wise Pending Balance" && (
                  <div className="mt-6 border rounded-lg p-6 bg-white shadow-sm space-y-6">
                    <div className="flex justify-end gap-3 border-b pb-4 no-print">
                      <Button variant="outline" size="sm" onClick={() => downloadCSV(reportRows, 'Indent Wise Report')}>Excel</Button>
                      <Button variant="outline" size="sm" onClick={() => printReportHTML('print-area-iw')}>Portrait</Button>
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm" onClick={() => printReportHTML('print-area-iw')}>PDF</Button>
                    </div>

                    <div id="print-area-iw" className="p-4 bg-white text-black font-sans space-y-6">
                      <div className="text-center space-y-1">
                        <h2 className="text-xl font-bold">RAMKRISHNA SERVICE CENTRE</h2>
                        <p className="text-sm">PRIYOR MORE.JOYNAGAR</p>
                        <p className="text-sm">24 PARGANA SOUTH</p>
                        <p className="text-sm">GSTIN: 19ABEPH6853K1Z9</p>
                        <p className="text-sm font-semibold">Mob: 9002296939</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                        <div>
                          <span className="font-semibold text-gray-600">Organization:</span> <span className="font-bold text-base">{reportDataHeader.customer_name || values.i_organization}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-600">Indent Selected:</span> <span className="font-semibold">{values.i_indentNo || "All"}</span>
                        </div>
                      </div>

                      <h3 className="text-center text-md font-bold bg-gray-100 border py-1.5 uppercase tracking-wide">Indent Wise Pending Balance</h3>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px] border-collapse border">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="border p-2 text-left">Indent No</th>
                              <th className="border p-2 text-left">Date</th>
                              <th className="border p-2 text-left">Product</th>
                              <th className="border p-2 text-right">Qty (lts)</th>
                              <th className="border p-2 text-right font-semibold">Amount</th>
                              <th className="border p-2 text-right">Pending Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportRows.map((row, idx) => (
                              <tr key={idx} className="border hover:bg-gray-50/50">
                                <td className="p-2 border font-bold">{row.indent_no}</td>
                                <td className="p-2 border whitespace-nowrap">{row.date}</td>
                                <td className="p-2 border">{row.product}</td>
                                <td className="p-2 border text-right">{row.qty ? parseFloat(row.qty).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right">{row.amount ? parseFloat(row.amount).toFixed(3) : "0.00"}</td>
                                <td className="p-2 border text-right font-bold text-emerald-600">{row.pending_balance ? parseFloat(row.pending_balance).toFixed(3) : "0.00"}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-bold border text-gray-900">
                              <td className="p-2 border" colSpan={3}>Total :</td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right">
                                {reportRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(3)}
                              </td>
                              <td className="p-2 border text-right text-emerald-600 font-extrabold">
                                {reportRows.reduce((sum, r) => sum + (Number(r.pending_balance) || 0), 0).toFixed(3)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {reportRows && selected !== 'Customer Account Statement' && (
                  <div className="mt-6 border rounded p-4">
                    <h4 className="font-semibold mb-3">Report Results</h4>
                    <div className="overflow-auto max-h-64">
                      <table className="w-full text-sm table-auto">
                        <thead>
                          <tr className="text-left">
                            {(reportColumns || Object.keys(reportRows[0] || {})).map((c) => (
                              <th key={c} className="pr-4">{c.replace(/_/g, ' ')}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportRows.map((row, idx) => {
                            const cols = (reportColumns || Object.keys(row));
                            return (
                              <tr key={row.id || idx} className="border-t">
                                {cols.map((c) => (
                                  <td key={c} className="pr-4 py-2">{String(row[c] ?? '')}</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

